import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Mail, Send, AlertCircle, CheckCircle, Users, UserCheck, Building, ChevronDown, ChevronUp, Eye, Plus, X } from "lucide-react";
import axios from 'axios';
import './TrainingMailSender.css';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const apiService = {
  post: (url, data, config = {}) => {
    const token = localStorage.getItem('access_token');
    return axios.post(`${API_BASE_URL}${url}`, data, {
      ...config,
      headers: {
        Authorization: `Bearer ${token}`,
        ...config.headers
      }
    });
  },
  get: (url, config = {}) => {
    const token = localStorage.getItem('access_token');
    return axios.get(`${API_BASE_URL}${url}`, {
      ...config,
      headers: {
        Authorization: `Bearer ${token}`,
        ...config.headers
      }
    });
  }
};

const TrainingMailSender = ({ isOpen, onClose, trainingData, statusChange, onSuccess }) => {
  console.log('=== TRAINING MAIL SENDER OPENED ===');
  console.log('trainingData structure:', trainingData);
  console.log('trainingData.users:', trainingData?.users);
  console.log('first user in trainingData:', trainingData?.users?.[0]);
  console.log('statusChange:', statusChange);
  console.log('TrainingMailSender render - new version');
  console.log('TrainingMailSender trainingData:', trainingData);
  
  // State for email recipients - updated component
  const [notifyParticipants, setNotifyParticipants] = useState(true);
  const [notifyManagers, setNotifyManagers] = useState(false);
  const [notifySupervisors, setNotifySupervisors] = useState(false);
  const [notifyCoordinators, setNotifyCoordinators] = useState(false);
  const [notifyTrainingCenter, setNotifyTrainingCenter] = useState(false); // Only for approved status
  
  // Additional states for participant_changes mode
  const [notifyManagersAdded, setNotifyManagersAdded] = useState(false);
  const [notifyManagersRemoved, setNotifyManagersRemoved] = useState(false);
  const [notifySupervisorsAdded, setNotifySupervisorsAdded] = useState(false);
  const [notifySupervisorsRemoved, setNotifySupervisorsRemoved] = useState(false);
  const [notifyCoordinatorsAdded, setNotifyCoordinatorsAdded] = useState(false);
  const [notifyCoordinatorsRemoved, setNotifyCoordinatorsRemoved] = useState(false);
  
  // Additional states for hybrid changes + participant_changes mode
  const [notifyManagersChanges, setNotifyManagersChanges] = useState(false);
  const [notifySupervisorsChanges, setNotifySupervisorsChanges] = useState(false);
  const [notifyCoordinatorsChanges, setNotifyCoordinatorsChanges] = useState(false);
  
  // State for individual participant selection
  const [selectedParticipants, setSelectedParticipants] = useState(new Set());
  const [participantsExpanded, setParticipantsExpanded] = useState(false);
  
  // State for participant change mode
  const [participantChangeMode, setParticipantChangeMode] = useState(null); // 'added', 'removed', or null
  const [managersExpanded, setManagersExpanded] = useState(true);
  
  // State for select all managers/supervisors/coordinators
  const [selectAllSupervisors, setSelectAllSupervisors] = useState(false);
  
  // State for message preview
  const [selectedMessageRecipient, setSelectedMessageRecipient] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // State for sending emails
  const [sending, setSending] = useState(false);
  const [sendingProgress, setSendingProgress] = useState(0);
  const [sendingStep, setSendingStep] = useState('');
  const [sentEmails, setSentEmails] = useState([]);
  const [totalEmails, setTotalEmails] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // State for additional supervisors - now linked to specific supervisors
  const [additionalSupervisors, setAdditionalSupervisors] = useState({}); // {supervisorId: [additional users]}
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [selectedSupervisorForAdditional, setSelectedSupervisorForAdditional] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Get template types based on status change
  const getTemplateTypes = () => {
    if (statusChange?.type === 'reminder') {
      return {
        participants: 'participants_reminder',
        supervisors: 'supervisors_reminder'
      };
    } else if (statusChange?.to === 'approved') {
      return {
        participants: 'participants_invitation',
        supervisors: 'supervisors_invitation',
        training_center: 'training_center_confirmation'
      };
    } else if (statusChange?.to === 'cancelled') {
      return {
        participants: 'participants_cancellation',
        supervisors: 'supervisors_cancellation'
      };
    } else if (statusChange?.type === 'participant_changes') {
      return {
        participants_added: 'participants_invitation', // New participants get invitation
        participants_removed: 'participants_cancellation', // Removed participants get cancellation  
        supervisors_added: 'supervisors_invitation', // Supervisors notified about added employees
        supervisors_removed: 'supervisors_cancellation', // Supervisors notified about removed employees
        training_center: 'training_center_changes' // Use existing template for participant list updates
      };
    } else if (statusChange?.type === 'changes') {
      return {
        participants: 'participants_changes',
        participants_added: 'participants_invitation', // New participants get invitation
        participants_removed: 'participants_cancellation', // Removed participants get cancellation
        supervisors: 'supervisors_changes', // For unchanged employees
        supervisors_added: 'supervisors_invitation', // Supervisors notified about added employees
        supervisors_removed: 'supervisors_cancellation', // Supervisors notified about removed employees
        training_center: 'training_center_changes'
      };
    }
    return {};
  };

  // Extract unique supervisors by role from participants
  const getUniqueManagersAndSupervisors = () => {
    console.log('🔍 getUniqueManagersAndSupervisors called with statusChange:', statusChange);
    console.log('🔍 statusChange.type:', statusChange?.type);
    console.log('🔍 statusChange.participantChanges:', statusChange?.participantChanges);
    console.log('🔍 statusChange.participantChanges?.hasChanges:', statusChange?.participantChanges?.hasChanges);
    
    const managers = new Map();
    const supervisors = new Map();
    const coordinators = new Map();
    
    // For participant_changes mode, we need separate lists for added and removed
    const managersAdded = new Map();
    const supervisorsAdded = new Map();
    const coordinatorsAdded = new Map();
    
    const managersRemoved = new Map();
    const supervisorsRemoved = new Map();
    const coordinatorsRemoved = new Map();
    
    // For hybrid mode (changes + participant_changes), we need lists for unchanged participants
    const managersChanges = new Map();
    const supervisorsChanges = new Map();
    const coordinatorsChanges = new Map();
    
    // Process all current training participants
    trainingData?.users_data?.forEach(user => {
      // Add manager
      if (user.manager) {
        managers.set(user.manager.id, user.manager);
      }
      // Add training supervisor
      if (user.training_supervisor) {
        supervisors.set(user.training_supervisor.id, user.training_supervisor);
      }
      // Add training coordinator  
      if (user.training_coordinator) {
        coordinators.set(user.training_coordinator.id, user.training_coordinator);
      }
    });
    
    // Process added participants for participant_changes and hybrid changes mode
    if ((statusChange?.type === 'participant_changes' || statusChange?.type === 'changes') && statusChange.participantChanges?.added) {
      statusChange.participantChanges.added.forEach(user => {
        // Find the full user data from users_data
        const fullUserData = trainingData?.users_data?.find(u => u.id === user.id) || user;
        
        if (fullUserData.manager) {
          managersAdded.set(fullUserData.manager.id, fullUserData.manager);
        }
        if (fullUserData.training_supervisor) {
          supervisorsAdded.set(fullUserData.training_supervisor.id, fullUserData.training_supervisor);
        }
        if (fullUserData.training_coordinator) {
          coordinatorsAdded.set(fullUserData.training_coordinator.id, fullUserData.training_coordinator);
        }
      });
    }
    
    // Process removed participants for participant_changes and hybrid changes mode  
    if ((statusChange?.type === 'participant_changes' || statusChange?.type === 'changes') && statusChange.participantChanges?.removed) {
      statusChange.participantChanges.removed.forEach(user => {
        // For removed users, we might need to get their supervisor info from the user object itself
        // since they might not be in users_data anymore
        if (user.manager) {
          managersRemoved.set(user.manager.id, user.manager);
        }
        if (user.training_supervisor) {
          supervisorsRemoved.set(user.training_supervisor.id, user.training_supervisor);
        }
        if (user.training_coordinator) {
          coordinatorsRemoved.set(user.training_coordinator.id, user.training_coordinator);
        }
      });
    }
    
    // Process unchanged participants for changes mode (those who stayed in training)
    if (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges) {
      // Get IDs of added and removed participants
      const addedIds = statusChange.participantChanges?.added?.map(u => u.id) || [];
      const removedIds = statusChange.participantChanges?.removed?.map(u => u.id) || [];
      
      // Find participants who are neither added nor removed (they stayed and get changes notification)
      trainingData?.users_data?.forEach(user => {
        if (!addedIds.includes(user.id) && !removedIds.includes(user.id)) {
          // This participant stayed in training and should get changes notification
          if (user.manager) {
            managersChanges.set(user.manager.id, user.manager);
          }
          if (user.training_supervisor) {
            supervisorsChanges.set(user.training_supervisor.id, user.training_supervisor);
          }
          if (user.training_coordinator) {
            coordinatorsChanges.set(user.training_coordinator.id, user.training_coordinator);
          }
        }
      });
    }
    
    return {
      managers: Array.from(managers.values()),
      supervisors: Array.from(supervisors.values()),
      coordinators: Array.from(coordinators.values()),
      // Separate lists for participant_changes mode
      managersAdded: Array.from(managersAdded.values()),
      supervisorsAdded: Array.from(supervisorsAdded.values()),
      coordinatorsAdded: Array.from(coordinatorsAdded.values()),
      managersRemoved: Array.from(managersRemoved.values()),
      supervisorsRemoved: Array.from(supervisorsRemoved.values()),
      coordinatorsRemoved: Array.from(coordinatorsRemoved.values()),
      // Additional lists for hybrid mode (changes + participant_changes)
      managersChanges: Array.from(managersChanges.values()),
      supervisorsChanges: Array.from(supervisorsChanges.values()),
      coordinatorsChanges: Array.from(coordinatorsChanges.values())
    };
  };

  const { 
    managers, supervisors, coordinators,
    managersAdded, supervisorsAdded, coordinatorsAdded,
    managersRemoved, supervisorsRemoved, coordinatorsRemoved,
    managersChanges, supervisorsChanges, coordinatorsChanges
  } = getUniqueManagersAndSupervisors();
  const templateTypes = getTemplateTypes();

  // Handle individual participant selection
  const handleParticipantToggle = (participantId) => {
    setSelectedParticipants(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(participantId)) {
        newSelected.delete(participantId);
      } else {
        newSelected.add(participantId);
      }
      
      // Auto-update main checkbox based on selection
      if (newSelected.size === 0) {
        setNotifyParticipants(false);
      } else if (newSelected.size > 0 && !notifyParticipants) {
        setNotifyParticipants(true);
      }
      
      return newSelected;
    });
  };


  // Reset state when dialog opens/closes or status changes
  useEffect(() => {
    if (isOpen) {
      if (statusChange?.type === 'reminder') {
        // Reminder mode: All participants selected by default, supervisors unselected
        setNotifyParticipants(true);
        setNotifyManagers(false);
        setNotifySupervisors(false);
        setNotifyCoordinators(false);
        setNotifyTrainingCenter(false);
      } else {
        setNotifyParticipants(true);
        setNotifyManagers(false);
        setNotifySupervisors(false);
        setNotifyCoordinators(false);
        // Training Center always disabled by default
        setNotifyTrainingCenter(false);
      }
      
      // Initialize participants selection based on change type
      let initialParticipantIds;
      let initialChangeMode = null;
      
      if (statusChange?.type === 'reminder') {
        // For reminder mode, select all current training participants
        initialParticipantIds = new Set(trainingData?.users_data?.map(u => u.id) || []);
        initialChangeMode = null;
      } else if (statusChange?.type === 'participant_changes' && statusChange.participantChanges?.hasChanges) {
        // For participant changes mode, select ALL affected users (both added and removed)
        const addedIds = statusChange.participantChanges?.added?.map(u => u.id) || [];
        const removedIds = statusChange.participantChanges?.removed?.map(u => u.id) || [];
        
        // Select all participants that were changed  
        initialParticipantIds = new Set([...addedIds, ...removedIds]);
        initialChangeMode = 'added'; // Start with added by default
      } else if (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges) {
        // For hybrid changes mode, select ALL participants (added + removed + unchanged)
        const addedIds = statusChange.participantChanges?.added?.map(u => u.id) || [];
        const removedIds = statusChange.participantChanges?.removed?.map(u => u.id) || [];
        const unchangedParticipants = trainingData?.users_data?.filter(user => 
          !addedIds.includes(user.id) && !removedIds.includes(user.id)
        ) || [];
        const unchangedIds = unchangedParticipants.map(u => u.id);
        
        // Select ALL participants by default in hybrid mode
        initialParticipantIds = new Set([...addedIds, ...removedIds, ...unchangedIds]);
        initialChangeMode = 'all'; // Special mode for selecting all types
      } else {
        // Default behavior - all participants
        initialParticipantIds = new Set(trainingData?.users_data?.map(u => u.id) || []);
      }
      
      setSelectedParticipants(initialParticipantIds);
      setParticipantChangeMode(initialChangeMode);
      setParticipantsExpanded(false);
      
      setResult(null);
      setError(null);
      setSending(false);
      setSelectedMessageRecipient(null);
      setTemplatePreview(null);
      
      // Load all users when dialog opens
      fetchAllUsers();
      // Reset additional supervisors
      setAdditionalSupervisors({});
    }
  }, [isOpen, statusChange, trainingData?.users_data]);
  
  // Fetch all users for additional supervisor selection
  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await apiService.get('/users/');
      const users = response.data.results || response.data;
      setAllUsers(users.filter(user => user.is_active));
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };
  
  // Add additional supervisor for specific supervisor
  const addAdditionalSupervisor = (user) => {
    if (!selectedSupervisorForAdditional) return;
    
    const supervisorId = selectedSupervisorForAdditional.id;
    setAdditionalSupervisors(prev => ({
      ...prev,
      [supervisorId]: [...(prev[supervisorId] || []), user]
    }));
    setShowUserSelector(false);
    setSelectedSupervisorForAdditional(null);
  };
  
  // Remove additional supervisor
  const removeAdditionalSupervisor = (supervisorId, userId) => {
    setAdditionalSupervisors(prev => ({
      ...prev,
      [supervisorId]: (prev[supervisorId] || []).filter(u => u.id !== userId)
    }));
  };
  
  // Open user selector for specific supervisor
  const openUserSelectorForSupervisor = (supervisor) => {
    setSelectedSupervisorForAdditional(supervisor);
    setShowUserSelector(true);
  };
  
  // Get available users (not already selected for this supervisor)
  const getAvailableUsers = () => {
    if (!selectedSupervisorForAdditional) return [];
    
    const supervisorId = selectedSupervisorForAdditional.id;
    const alreadySelectedIds = (additionalSupervisors[supervisorId] || []).map(u => u.id);
    
    // Also exclude the supervisor himself and already added users
    return allUsers.filter(user => 
      user.id !== supervisorId && 
      !alreadySelectedIds.includes(user.id)
    );
  };


  // Handle streaming email send with real-time progress
  const handleStreamingEmailSend = async (payload) => {
    setSendingProgress(0);
    setSendingStep('');
    setSentEmails([]);
    setTotalEmails(0);
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/send-training-emails-stream/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const text = decoder.decode(value);
        const lines = text.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              
              switch (data.type) {
                case 'init':
                  setTotalEmails(data.total_emails);
                  setSendingStep(data.step);
                  break;
                
                case 'progress':
                  setSendingProgress(data.progress);
                  setSendingStep(data.step);
                  break;
                
                case 'email_sent':
                  setSentEmails(prev => [...prev, {
                    recipient: data.recipient,
                    timestamp: new Date().toLocaleTimeString()
                  }]);
                  setSendingProgress(data.progress);
                  setSendingStep(`📧 Sent to: ${data.recipient}`);
                  break;
                
                case 'email_error':
                  setSentEmails(prev => [...prev, {
                    recipient: `❌ ERROR: ${data.message}`,
                    timestamp: new Date().toLocaleTimeString(),
                    error: true
                  }]);
                  break;
                
                case 'complete':
                  setSendingProgress(100);
                  setSendingStep('🎉 Sending complete!');
                  setResult(data.results);
                  
                  console.log('📧 Streaming complete, results:', data.results);
                  
                  // Call success callback with proper format
                  if (onSuccess) {
                    console.log('📧 Calling onSuccess with results:', data.results);
                    onSuccess({
                      results: data.results,
                      success: true
                    });
                  }
                  
                  // Close modal after delay
                  setTimeout(() => {
                    onClose();
                  }, 2000);
                  break;
                
                case 'error':
                  throw new Error(data.message);
                
                default:
                  console.log('Unknown message type:', data.type);
              }
            } catch (parseError) {
              console.error('Error parsing streaming data:', parseError, line);
            }
          }
        }
      }
    } catch (streamError) {
      console.error('Streaming error:', streamError);
      throw streamError;
    }
  };

  // Handle sending emails
  const handleSendEmails = async () => {
    setSending(true);
    setError(null);
    setResult(null);

    try {

      // Handle participant changes for changes mode
      const participantChanges = statusChange?.participantChanges;
      let participantIds = [];
      let participantIdsAdded = [];
      let participantIdsRemoved = [];

      if (statusChange?.type === 'participant_changes') {
        // Participant changes mode - send to both added and removed participants automatically
        participantIdsAdded = statusChange.participantChanges?.added?.map(u => u.id) || [];
        participantIdsRemoved = statusChange.participantChanges?.removed?.map(u => u.id) || [];
        participantIds = []; // No regular participants in this mode
      } else if (statusChange?.type === 'changes' && participantChanges?.hasChanges) {
        // In changes mode with participant changes, handle based on selected mode
        if (participantChangeMode === 'added') {
          // Send invitations to newly added participants
          participantIdsAdded = notifyParticipants ? Array.from(selectedParticipants) : [];
          participantIdsRemoved = [];
          participantIds = [];
        } else if (participantChangeMode === 'removed') {
          // Send cancellations to removed participants
          participantIdsAdded = [];
          participantIdsRemoved = notifyParticipants ? Array.from(selectedParticipants) : [];
          participantIds = [];
        } else if (participantChangeMode === 'changes') {
          // Send changes notifications to unchanged participants
          participantIdsAdded = [];
          participantIdsRemoved = [];
          participantIds = notifyParticipants ? Array.from(selectedParticipants) : [];
        } else if (participantChangeMode === 'all') {
          // All types selected - separate by their change types
          const addedIds = participantChanges.added?.map(u => u.id) || [];
          const removedIds = participantChanges.removed?.map(u => u.id) || [];
          
          // Filter selected participants by their change type
          participantIdsAdded = addedIds.filter(id => selectedParticipants.has(id));
          participantIdsRemoved = removedIds.filter(id => selectedParticipants.has(id));
          participantIds = Array.from(selectedParticipants).filter(id => 
            !addedIds.includes(id) && !removedIds.includes(id)
          );
        } else {
          // Default behavior
          participantIdsAdded = [];
          participantIdsRemoved = [];
          participantIds = [];
        }
      } else {
        // Normal mode - all selected participants
        participantIds = notifyParticipants ? Array.from(selectedParticipants) : [];
      }

      // Collect all selected supervisor IDs - backend handles subordinate filtering
      const selectedSupervisorIds = [];
      
      if (statusChange?.type === 'participant_changes' || (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges)) {
        // For participant changes mode or hybrid mode, use the separate lists
        const supervisorSet = new Set();
        
        if (notifyManagersAdded) {
          managersAdded.forEach(m => supervisorSet.add(m.id));
        }
        if (notifyManagersRemoved) {
          managersRemoved.forEach(m => supervisorSet.add(m.id));
        }
        if (notifySupervisorsAdded) {
          supervisorsAdded.forEach(s => supervisorSet.add(s.id));
        }
        if (notifySupervisorsRemoved) {
          supervisorsRemoved.forEach(s => supervisorSet.add(s.id));
        }
        if (notifyCoordinatorsAdded) {
          coordinatorsAdded.forEach(c => supervisorSet.add(c.id));
        }
        if (notifyCoordinatorsRemoved) {
          coordinatorsRemoved.forEach(c => supervisorSet.add(c.id));
        }
        if (notifyManagersChanges) {
          managersChanges.forEach(m => supervisorSet.add(m.id));
        }
        if (notifySupervisorsChanges) {
          supervisorsChanges.forEach(s => supervisorSet.add(s.id));
        }
        if (notifyCoordinatorsChanges) {
          coordinatorsChanges.forEach(c => supervisorSet.add(c.id));
        }
        
        selectedSupervisorIds.push(...Array.from(supervisorSet));
      } else {
        // Normal mode
        if (notifyManagers) {
          selectedSupervisorIds.push(...managers.map(m => m.id));
        }
        if (notifySupervisors) {
          selectedSupervisorIds.push(...supervisors.map(s => s.id));
        }
        if (notifyCoordinators) {
          selectedSupervisorIds.push(...coordinators.map(c => c.id));
        }
      }
      
      // Prepare supervisor substitutes mapping and supervisor-subordinates mapping for backend
      const supervisorSubstitutes = {};
      let supervisorSubordinates = {}; // Map of supervisor_id -> [list of subordinate participant IDs]
      
      // Build supervisor-subordinates mapping from current training participants
      const buildSupervisorSubordinatesMap = (usersList, targetSupervisorIds) => {
        const mapping = {};
        
        usersList.forEach(user => {
          // Check all supervisor roles
          [user.manager, user.training_supervisor, user.training_coordinator].forEach(supervisor => {
            if (supervisor && targetSupervisorIds.includes(supervisor.id)) {
              if (!mapping[supervisor.id]) {
                mapping[supervisor.id] = [];
              }
              mapping[supervisor.id].push(user.id);
            }
          });
        });
        
        return mapping;
      };
      
      // Get all supervisor IDs (without additional ones yet)
      const originalSupervisorIds = [];
      if (notifyManagers) {
        originalSupervisorIds.push(...managers.map(m => m.id));
      }
      if (notifySupervisors) {
        originalSupervisorIds.push(...supervisors.map(s => s.id));
      }
      if (notifyCoordinators) {
        originalSupervisorIds.push(...coordinators.map(c => c.id));
      }
      
      // Build mapping for ALL training participants (not just selected ones)
      // Supervisors should see all their subordinates who are training participants,
      // regardless of whether those participants are selected to receive emails
      const allTrainingParticipants = trainingData?.users_data || [];
      
      supervisorSubordinates = buildSupervisorSubordinatesMap(allTrainingParticipants, originalSupervisorIds);
      
      // Handle additional supervisors
      Object.keys(additionalSupervisors).forEach(supervisorId => {
        const additionalUsers = additionalSupervisors[supervisorId] || [];
        const originalSupervisorIdInt = parseInt(supervisorId);
        
        additionalUsers.forEach(user => {
          selectedSupervisorIds.push(user.id);
          // Map substitute to original supervisor so backend knows whose employees to include
          supervisorSubstitutes[user.id] = originalSupervisorIdInt;
          // Additional supervisor inherits the same subordinates as original supervisor
          supervisorSubordinates[user.id] = supervisorSubordinates[originalSupervisorIdInt] || [];
          
          console.log(`📧 Frontend: Substitute ${user.id} inherits subordinates from ${originalSupervisorIdInt}:`, supervisorSubordinates[user.id]);
        });
      });

      // For Training Center, we need all current participants (after changes)
      let trainingCenterParticipantIds = [];
      if (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges) {
        // In hybrid mode, Training Center should see all current participants
        // Start with all users_data, then remove the ones being removed, add the new ones
        const currentParticipants = new Set(trainingData?.users_data?.map(u => u.id) || []);
        
        // Remove the ones being removed
        participantIdsRemoved.forEach(id => currentParticipants.delete(id));
        
        // Add the new ones
        participantIdsAdded.forEach(id => currentParticipants.add(id));
        
        trainingCenterParticipantIds = Array.from(currentParticipants);
      } else {
        // Normal mode - use participant_ids
        trainingCenterParticipantIds = participantIds;
      }

      const payload = {
        training_id: trainingData.id,
        participant_ids: participantIds,
        participant_ids_added: participantIdsAdded,
        participant_ids_removed: participantIdsRemoved,
        supervisor_ids: selectedSupervisorIds, // Backend already handles subordinate filtering
        supervisor_substitutes: supervisorSubstitutes, // Map of substitute_id -> original_supervisor_id
        supervisor_subordinates: supervisorSubordinates, // Map of supervisor_id -> [subordinate participant IDs]
        training_center_id: notifyTrainingCenter ? trainingData.training_center_qualification_data?.training_center?.id : null,
        training_center_participant_ids: trainingCenterParticipantIds, // All current participants for Training Center
        template_types: {},
        // Add changes data for changes mode
        changes_summary: statusChange?.type === 'changes' ? formatChangesText(statusChange.changes) : null
      };

      // Add template types for selected recipients
      if (participantIds.length > 0 && templateTypes.participants) {
        payload.template_types.participants = templateTypes.participants;
      }
      
      // Add template types for participant changes
      if (participantIdsAdded.length > 0 && templateTypes.participants_added) {
        payload.template_types.participants_added = templateTypes.participants_added;
      }
      if (participantIdsRemoved.length > 0 && templateTypes.participants_removed) {
        payload.template_types.participants_removed = templateTypes.participants_removed;
      }
      
      // Handle supervisor templates based on mode
      if (statusChange?.type === 'participant_changes') {
        // Use different templates for added vs removed supervisor notifications
        if ((notifyManagersAdded || notifySupervisorsAdded || notifyCoordinatorsAdded) && templateTypes.supervisors_added) {
          payload.template_types.supervisors_added = templateTypes.supervisors_added;
        }
        if ((notifyManagersRemoved || notifySupervisorsRemoved || notifyCoordinatorsRemoved) && templateTypes.supervisors_removed) {
          payload.template_types.supervisors_removed = templateTypes.supervisors_removed;
        }
      } else if (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges) {
        // Hybrid mode - handle all three types
        if ((notifyManagersAdded || notifySupervisorsAdded || notifyCoordinatorsAdded) && templateTypes.supervisors_added) {
          payload.template_types.supervisors_added = templateTypes.supervisors_added;
        }
        if ((notifyManagersRemoved || notifySupervisorsRemoved || notifyCoordinatorsRemoved) && templateTypes.supervisors_removed) {
          payload.template_types.supervisors_removed = templateTypes.supervisors_removed;
        }
        if ((notifyManagersChanges || notifySupervisorsChanges || notifyCoordinatorsChanges) && templateTypes.supervisors) {
          payload.template_types.supervisors = templateTypes.supervisors;
        }
      } else {
        // Normal supervisor templates
        if ((notifyManagers || notifySupervisors || notifyCoordinators) && templateTypes.supervisors) {
          payload.template_types.supervisors = templateTypes.supervisors;
        }
      }
      if (notifyTrainingCenter && templateTypes.training_center) {
        payload.template_types.training_center = templateTypes.training_center;
      }

      console.log('📧 Sending training emails:', payload);
      console.log('📊 Participant breakdown:');
      console.log('  - participant_ids (changes):', participantIds);
      console.log('  - participant_ids_added:', participantIdsAdded);
      console.log('  - participant_ids_removed:', participantIdsRemoved);
      console.log('📊 Supervisors:');
      console.log('  - supervisor_ids:', selectedSupervisorIds);
      console.log('  - supervisor_substitutes:', supervisorSubstitutes);
      console.log('  - supervisor_subordinates:', supervisorSubordinates);
      console.log('📊 Key insight: supervisor_subordinates contains ALL training participants for each supervisor,');
      console.log('    regardless of whether those participants are selected to receive emails (participant_ids)');
      console.log('📊 Backend should use supervisor_subordinates for employee lists in supervisor emails,');
      console.log('    NOT participant_ids which only controls who gets participant emails');
      console.log('📊 Templates being used:');
      console.log('  - participants:', payload.template_types.participants);
      console.log('  - participants_added:', payload.template_types.participants_added);
      console.log('  - participants_removed:', payload.template_types.participants_removed);
      console.log('  - supervisors:', payload.template_types.supervisors);
      console.log('  - supervisors_added:', payload.template_types.supervisors_added);
      console.log('  - supervisors_removed:', payload.template_types.supervisors_removed);
      console.log('📊 Training Center:');
      console.log('  - training_center_participant_ids:', trainingCenterParticipantIds);
      console.log('  - length:', trainingCenterParticipantIds.length);
      
      // Use streaming endpoint for real-time progress
      await handleStreamingEmailSend(payload);

    } catch (error) {
      console.error('Error sending emails:', error);
      setError(error.response?.data?.error || 'Failed to send emails');
    } finally {
      setSending(false);
      setSendingProgress(0);
      setSendingStep('');
      setSentEmails([]);
      setTotalEmails(0);
    }
  };

  // Format changes for display
  const formatChangesText = (changes) => {
    if (!changes || changes.length === 0) return '';
    
    return changes.map(change => 
      `${change.label}:\n  • Z: ${change.oldValue}\n  • NA: ${change.newValue}`
    ).join('\n\n');
  };

  // Get status change message
  const getStatusChangeMessage = () => {
    if (statusChange?.type === 'reminder') {
      return `Send a reminder email about the upcoming training. Choose who you want to notify.`;
    } else if (statusChange?.to === 'approved') {
      return `Your training status has changed to ${statusChange?.to}. Choose who you want to notify about creating the new training.`;
    } else if (statusChange?.to === 'cancelled') {
      return `Your training status has changed to ${statusChange?.to}. Choose who you want to notify about cancelling the training.`;
    } else if (statusChange?.type === 'participant_changes') {
      const participantChanges = statusChange.participantChanges;
      const addedCount = participantChanges?.added?.length || 0;
      const removedCount = participantChanges?.removed?.length || 0;
      
      let message = 'Participant list has been updated for this approved training';
      
      const parts = [];
      if (addedCount > 0) parts.push(`${addedCount} participants added`);
      if (removedCount > 0) parts.push(`${removedCount} participants removed`);
      if (parts.length > 0) {
        message += ` (${parts.join(', ')})`;
      }
      
      return `${message}. Choose who you want to notify about these participant changes.`;
    } else if (statusChange?.type === 'changes') {
      const changesCount = statusChange.changes ? statusChange.changes.length : 0;
      const participantChanges = statusChange.participantChanges;
      const addedCount = participantChanges?.added?.length || 0;
      const removedCount = participantChanges?.removed?.length || 0;
      
      let message = 'Important changes have been made to the approved training';
      
      if (changesCount > 0 || addedCount > 0 || removedCount > 0) {
        const parts = [];
        if (changesCount > 0) parts.push(`${changesCount} field changes`);
        if (addedCount > 0) parts.push(`${addedCount} participants added`);
        if (removedCount > 0) parts.push(`${removedCount} participants removed`);
        message += ` (${parts.join(', ')})`;
      }
      
      return `${message}. Choose who you want to notify about these changes.`;
    }
    return `Your training status has changed to ${statusChange?.to}. Choose who you want to notify about this change.`;
  };

  

  // Get subordinates for a supervisor (for Message Preview)
  const getSupervisorSubordinates = (supervisorId, supervisorRole, recipient = null) => {
    // Check if this is an additional supervisor with original supervisor info
    let effectiveSupervisorId = supervisorId;
    let effectiveSupervisorRole = supervisorRole;
    
    if (recipient && recipient.originalSupervisorId) {
      // Use original supervisor's data for additional supervisors
      effectiveSupervisorId = recipient.originalSupervisorId;
      effectiveSupervisorRole = recipient.originalSupervisorRole;
      console.log(`📧 Message Preview: Supervisor ${supervisorId} is substitute for ${effectiveSupervisorId}, showing their subordinates`);
    }
    if (statusChange?.type === 'participant_changes' || (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges)) {
      // For participant_changes mode or hybrid mode, get subordinates from added/removed/changed lists
      const subordinates = { added: [], removed: [], changes: [] };
      
      // Check added participants
      statusChange.participantChanges?.added?.forEach(participant => {
        const fullParticipant = trainingData?.users_data?.find(u => u.id === participant.id) || participant;
        if ((effectiveSupervisorRole === 'Manager' && fullParticipant.manager?.id === effectiveSupervisorId) ||
            (effectiveSupervisorRole === 'Supervisor' && fullParticipant.training_supervisor?.id === effectiveSupervisorId) ||
            (effectiveSupervisorRole === 'Coordinator' && fullParticipant.training_coordinator?.id === effectiveSupervisorId)) {
          subordinates.added.push(participant);
        }
      });
      
      // Check removed participants
      statusChange.participantChanges?.removed?.forEach(participant => {
        if ((effectiveSupervisorRole === 'Manager' && participant.manager?.id === effectiveSupervisorId) ||
            (effectiveSupervisorRole === 'Supervisor' && participant.training_supervisor?.id === effectiveSupervisorId) ||
            (effectiveSupervisorRole === 'Coordinator' && participant.training_coordinator?.id === effectiveSupervisorId)) {
          subordinates.removed.push(participant);
        }
      });
      
      // Check unchanged participants (for hybrid changes mode)
      if (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges) {
        const addedIds = statusChange.participantChanges?.added?.map(u => u.id) || [];
        const removedIds = statusChange.participantChanges?.removed?.map(u => u.id) || [];
        
        trainingData?.users_data?.forEach(participant => {
          if (!addedIds.includes(participant.id) && !removedIds.includes(participant.id)) {
            // This participant stayed in training and gets changes notification
            if ((effectiveSupervisorRole === 'Manager' && participant.manager?.id === effectiveSupervisorId) ||
                (effectiveSupervisorRole === 'Supervisor' && participant.training_supervisor?.id === effectiveSupervisorId) ||
                (effectiveSupervisorRole === 'Coordinator' && participant.training_coordinator?.id === effectiveSupervisorId)) {
              subordinates.changes.push(participant);
            }
          }
        });
      }
      
      return subordinates;
    } else {
      // Normal mode - get from ALL training participants (not just selected ones)
      // Supervisors should see all their subordinates in training, regardless of email selection
      const subordinates = [];
      const allTrainingParticipants = trainingData?.users_data || [];
      
      allTrainingParticipants.forEach(participant => {
        if ((effectiveSupervisorRole === 'Manager' && participant.manager?.id === effectiveSupervisorId) ||
            (effectiveSupervisorRole === 'Supervisor' && participant.training_supervisor?.id === effectiveSupervisorId) ||
            (effectiveSupervisorRole === 'Coordinator' && participant.training_coordinator?.id === effectiveSupervisorId)) {
          subordinates.push(participant);
        }
      });
      
      return { participants: subordinates };
    }
  };

  // Handle message preview
  const handleShowMessage = async (recipient) => {
    setSelectedMessageRecipient(recipient);
    setLoadingPreview(true);
    setTemplatePreview(null);
    
    try {
      // Determine template type based on recipient and mode
      let templateType = '';
      
      if (statusChange?.type === 'participant_changes') {
        // Special handling for participant_changes mode
        if (recipient.type === 'participant') {
          // For participants, check if they were added or removed
          if (recipient.changeType === 'added') {
            templateType = templateTypes.participants_added; // 'participants_invitation'
          } else if (recipient.changeType === 'removed') {
            templateType = templateTypes.participants_removed; // 'participants_cancellation'
          }
        } else if (recipient.type === 'supervisor') {
          // For supervisors, they get different templates based on what they're notified about
          if (recipient.notificationTypes?.changes) {
            templateType = templateTypes.supervisors; // 'supervisors_changes'
          } else if (recipient.notificationTypes?.added && recipient.notificationTypes?.removed) {
            // If notified about both, default to invitation (you might want to handle this differently)
            templateType = templateTypes.supervisors_added; // 'supervisors_invitation'
          } else if (recipient.notificationTypes?.added) {
            templateType = templateTypes.supervisors_added; // 'supervisors_invitation'
          } else if (recipient.notificationTypes?.removed) {
            templateType = templateTypes.supervisors_removed; // 'supervisors_cancellation'
          }
        } else if (recipient.type === 'training_center') {
          templateType = templateTypes.training_center; // 'training_center_participants_update'
        }
      } else if (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges) {
        // Hybrid mode (changes with participant changes)
        if (recipient.type === 'participant') {
          // Check the changeType for participants
          if (recipient.changeType === 'added') {
            templateType = templateTypes.participants_added; // 'participants_invitation'
          } else if (recipient.changeType === 'removed') {
            templateType = templateTypes.participants_removed; // 'participants_cancellation'
          } else if (recipient.changeType === 'changes') {
            templateType = templateTypes.participants; // 'participants_changes'
          }
        } else if (recipient.type === 'supervisor') {
          // For supervisors in hybrid mode
          if (recipient.notificationTypes?.changes) {
            templateType = templateTypes.supervisors; // 'supervisors_changes'
          } else if (recipient.notificationTypes?.added) {
            templateType = templateTypes.supervisors_added; // 'supervisors_invitation'
          } else if (recipient.notificationTypes?.removed) {
            templateType = templateTypes.supervisors_removed; // 'supervisors_cancellation'
          }
        } else if (recipient.type === 'training_center') {
          templateType = templateTypes.training_center; // 'training_center_changes'
        }
      } else {
        // Normal mode (approved, cancelled, plain changes without participant changes)
        if (recipient.type === 'participant') {
          templateType = templateTypes.participants;
        } else if (recipient.type === 'supervisor') {
          templateType = templateTypes.supervisors;
        } else if (recipient.type === 'training_center') {
          templateType = templateTypes.training_center;
        }
      }
      
      if (templateType) {
        console.log('🔍 Fetching templates for type:', templateType);
        
        // First, get templates for this template type
        const templatesResponse = await apiService.get(`/email-templates/?template_type=${templateType}`);
        const templates = templatesResponse.data.results || templatesResponse.data;
        
        console.log('📧 Found templates:', templates);
        console.log('📧 Templates count:', templates.length);
        
        if (templates.length > 0) {
          // Use the first active template
          const activeTemplate = templates.find(t => t.is_active) || templates[0];
          console.log('✅ Selected template:', activeTemplate);
          
          // Get subordinates for supervisors to show accurate preview
          let previewParticipantChanges = null;
          let previewSubordinates = null;
          
          if (recipient.type === 'supervisor') {
            const subordinates = getSupervisorSubordinates(recipient.id, recipient.role, recipient);
            
            if (statusChange?.type === 'participant_changes' || (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges)) {
              // For participant_changes or hybrid mode, show only the specific subordinates based on notification type
              if (recipient.notificationTypes?.added && recipient.notificationTypes?.removed && recipient.notificationTypes?.changes) {
                // Show all - very rare case
                previewParticipantChanges = {
                  added: subordinates.added,
                  removed: subordinates.removed
                };
                previewSubordinates = [...subordinates.added, ...subordinates.removed, ...subordinates.changes];
              } else if (recipient.notificationTypes?.added && recipient.notificationTypes?.removed) {
                // Show both added and removed
                previewParticipantChanges = {
                  added: subordinates.added,
                  removed: subordinates.removed
                };
                previewSubordinates = [...subordinates.added, ...subordinates.removed];
              } else if (recipient.notificationTypes?.added) {
                previewParticipantChanges = {
                  added: subordinates.added,
                  removed: []
                };
                previewSubordinates = subordinates.added;
              } else if (recipient.notificationTypes?.removed) {
                previewParticipantChanges = {
                  added: [],
                  removed: subordinates.removed
                };
                previewSubordinates = subordinates.removed;
              } else if (recipient.notificationTypes?.changes) {
                // For changes notification, we don't need participant_changes, just subordinates
                previewParticipantChanges = null;
                previewSubordinates = subordinates.changes;
              }
            } else {
              // Normal mode - show all subordinates
              previewSubordinates = subordinates.participants;
            }
          } else if (recipient.type === 'training_center') {
            // For training center, show all participants
            if (statusChange?.type === 'participant_changes') {
              previewParticipantChanges = {
                added: statusChange.participantChanges?.added || [],
                removed: statusChange.participantChanges?.removed || []
              };
            }
          } else {
            // For participants, use original data
            if (statusChange?.type === 'participant_changes') {
              previewParticipantChanges = {
                added: statusChange.participantChanges?.added || [],
                removed: statusChange.participantChanges?.removed || []
              };
            }
          }

          // Create employee_list for supervisor templates
          let employeeList = '';
          if (previewSubordinates && previewSubordinates.length > 0) {
            employeeList = previewSubordinates.map(subordinate => {
              const name = subordinate.first_name && subordinate.last_name 
                ? `${subordinate.first_name} ${subordinate.last_name}` 
                : subordinate.username;
              return `- ${name}`;
            }).join('\n');
          }

          // Get template preview with training data
          const previewPayload = {
            training_id: trainingData.id,
            recipient_name: recipient.name,
            recipient_email: recipient.email,
            user_id: recipient.id,
            // Add changes summary for changes mode
            changes_summary: statusChange?.type === 'changes' ? formatChangesText(statusChange.changes) : null,
            // Add participant changes - filtered for supervisors
            participant_changes: previewParticipantChanges,
            // Add custom employee_list for supervisor templates - override backend's default
            employee_list_override: employeeList,
            // Add subordinates count
            subordinates_count: previewSubordinates ? previewSubordinates.length : 0
          };
          
          console.log('🚀 Sending preview request with payload:', previewPayload);
          
          const previewResponse = await apiService.post(`/email-templates/${activeTemplate.id}/preview/`, previewPayload);
          
          console.log('📨 Preview response:', previewResponse.data);
          
          const finalPreview = {
            ...previewResponse.data,
            template_name: activeTemplate.name,
            template_type: templateType
          };
          
          console.log('🎯 Final preview data:', finalPreview);
          setTemplatePreview(finalPreview);
        } else {
          console.log('❌ No templates found for type:', templateType);
          setTemplatePreview({
            error: 'No email template found',
            details: `No active template found for type: ${templateType}`
          });
        }
      }
    } catch (error) {
      console.error('Error fetching email preview:', error);
      setTemplatePreview({
        error: 'Failed to load email preview',
        details: error.response?.data?.error || error.message
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  // Add recipient ID to the selected recipients
  const getSelectedRecipientsWithIds = () => {
    const recipients = {
      participants: [],
      supervisors: [],
      trainingCenter: null
    };
    
    // Add selected participants
    if (statusChange?.type === 'participant_changes' || (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges)) {
      // In participant_changes mode or hybrid mode, show all participants with their change types
      const addedParticipants = statusChange?.participantChanges?.added || [];
      const removedParticipants = statusChange?.participantChanges?.removed || [];
      
      // Add added participants if selected
      addedParticipants.forEach(user => {
        if (selectedParticipants.has(user.id)) {
          recipients.participants.push({
            id: user.id,
            name: user.first_name && user.last_name 
              ? `${user.first_name} ${user.last_name}` 
              : user.username,
            email: user.email,
            type: 'participant',
            changeType: 'added'
          });
        }
      });
      
      // Add removed participants if selected
      removedParticipants.forEach(user => {
        if (selectedParticipants.has(user.id)) {
          recipients.participants.push({
            id: user.id,
            name: user.first_name && user.last_name 
              ? `${user.first_name} ${user.last_name}` 
              : user.username,
            email: user.email,
            type: 'participant',
            changeType: 'removed'
          });
        }
      });
      
      // Add unchanged participants if in hybrid mode
      if (statusChange?.type === 'changes') {
        const addedIds = addedParticipants.map(u => u.id);
        const removedIds = removedParticipants.map(u => u.id);
        
        trainingData?.users_data?.forEach(user => {
          if (!addedIds.includes(user.id) && !removedIds.includes(user.id) && selectedParticipants.has(user.id)) {
            recipients.participants.push({
              id: user.id,
              name: user.first_name && user.last_name 
                ? `${user.first_name} ${user.last_name}` 
                : user.username,
              email: user.email,
              type: 'participant',
              changeType: 'changes'
            });
          }
        });
      }
    } else {
      // Normal mode - use current training participants
      if (notifyParticipants) {
        trainingData?.users_data?.forEach(user => {
          if (selectedParticipants.has(user.id)) {
            recipients.participants.push({
              id: user.id,
              name: user.first_name && user.last_name 
                ? `${user.first_name} ${user.last_name}` 
                : user.username,
              email: user.email,
              type: 'participant'
            });
          }
        });
      }
    }
    
    // Add selected supervisors/managers/coordinators
    // Handle participant_changes, changes (hybrid), and normal mode
    if (statusChange?.type === 'participant_changes' || (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges)) {
      // In participant_changes mode or hybrid mode, create separate entries for each notification type
      // Process managers - create separate entries for added, removed, and changes
      if (notifyManagersAdded) {
        managersAdded.forEach(manager => {
          // Add entry for "added" notification
          recipients.supervisors.push({
            id: manager.id,
            uniqueKey: `${manager.id}_added`, // Unique key for React list
            name: manager.first_name && manager.last_name 
              ? `${manager.first_name} ${manager.last_name}` 
              : manager.username,
            email: manager.email,
            role: 'Manager',
            type: 'supervisor',
            notificationTypes: { added: true, removed: false, changes: false }
          });
        });
      }
      
      if (notifyManagersRemoved) {
        managersRemoved.forEach(manager => {
          // Add separate entry for "removed" notification
          recipients.supervisors.push({
            id: manager.id,
            uniqueKey: `${manager.id}_removed`, // Unique key for React list
            name: manager.first_name && manager.last_name 
              ? `${manager.first_name} ${manager.last_name}` 
              : manager.username,
            email: manager.email,
            role: 'Manager',
            type: 'supervisor',
            notificationTypes: { added: false, removed: true, changes: false }
          });
        });
      }
      
      if (notifyManagersChanges) {
        managersChanges.forEach(manager => {
          // Add separate entry for "changes" notification
          recipients.supervisors.push({
            id: manager.id,
            uniqueKey: `${manager.id}_changes`, // Unique key for React list
            name: manager.first_name && manager.last_name 
              ? `${manager.first_name} ${manager.last_name}` 
              : manager.username,
            email: manager.email,
            role: 'Manager',
            type: 'supervisor',
            notificationTypes: { added: false, removed: false, changes: true }
          });
        });
      }
      
      // Process supervisors - create separate entries for added and removed
      if (notifySupervisorsAdded) {
        supervisorsAdded.forEach(supervisor => {
          recipients.supervisors.push({
            id: supervisor.id,
            uniqueKey: `${supervisor.id}_added`,
            name: supervisor.first_name && supervisor.last_name 
              ? `${supervisor.first_name} ${supervisor.last_name}` 
              : supervisor.username,
            email: supervisor.email,
            role: 'Supervisor',
            type: 'supervisor',
            notificationTypes: { added: true, removed: false, changes: false }
          });
        });
      }
      
      if (notifySupervisorsRemoved) {
        supervisorsRemoved.forEach(supervisor => {
          recipients.supervisors.push({
            id: supervisor.id,
            uniqueKey: `${supervisor.id}_removed`,
            name: supervisor.first_name && supervisor.last_name 
              ? `${supervisor.first_name} ${supervisor.last_name}` 
              : supervisor.username,
            email: supervisor.email,
            role: 'Supervisor',
            type: 'supervisor',
            notificationTypes: { added: false, removed: true, changes: false }
          });
        });
      }
      
      if (notifySupervisorsChanges) {
        supervisorsChanges.forEach(supervisor => {
          recipients.supervisors.push({
            id: supervisor.id,
            uniqueKey: `${supervisor.id}_changes`,
            name: supervisor.first_name && supervisor.last_name 
              ? `${supervisor.first_name} ${supervisor.last_name}` 
              : supervisor.username,
            email: supervisor.email,
            role: 'Supervisor',
            type: 'supervisor',
            notificationTypes: { added: false, removed: false, changes: true }
          });
        });
      }
      
      // Process coordinators - create separate entries for added, removed, and changes
      if (notifyCoordinatorsAdded) {
        coordinatorsAdded.forEach(coordinator => {
          recipients.supervisors.push({
            id: coordinator.id,
            uniqueKey: `${coordinator.id}_added`,
            name: coordinator.first_name && coordinator.last_name 
              ? `${coordinator.first_name} ${coordinator.last_name}` 
              : coordinator.username,
            email: coordinator.email,
            role: 'Coordinator',
            type: 'supervisor',
            notificationTypes: { added: true, removed: false, changes: false }
          });
        });
      }
      
      if (notifyCoordinatorsRemoved) {
        coordinatorsRemoved.forEach(coordinator => {
          recipients.supervisors.push({
            id: coordinator.id,
            uniqueKey: `${coordinator.id}_removed`,
            name: coordinator.first_name && coordinator.last_name 
              ? `${coordinator.first_name} ${coordinator.last_name}` 
              : coordinator.username,
            email: coordinator.email,
            role: 'Coordinator',
            type: 'supervisor',
            notificationTypes: { added: false, removed: true, changes: false }
          });
        });
      }
      
      if (notifyCoordinatorsChanges) {
        coordinatorsChanges.forEach(coordinator => {
          recipients.supervisors.push({
            id: coordinator.id,
            uniqueKey: `${coordinator.id}_changes`,
            name: coordinator.first_name && coordinator.last_name 
              ? `${coordinator.first_name} ${coordinator.last_name}` 
              : coordinator.username,
            email: coordinator.email,
            role: 'Coordinator',
            type: 'supervisor',
            notificationTypes: { added: false, removed: false, changes: true }
          });
        });
      }
      
      // Note: We don't need to combine supervisors here anymore as we're pushing directly to recipients.supervisors
    } else {
      // Normal mode
      if (notifyManagers) {
        managers.forEach(manager => {
          recipients.supervisors.push({
            id: manager.id,
            name: manager.first_name && manager.last_name 
              ? `${manager.first_name} ${manager.last_name}` 
              : manager.username,
            email: manager.email,
            role: 'Manager',
            type: 'supervisor'
          });
        });
      }
      
      if (notifySupervisors) {
        supervisors.forEach(supervisor => {
          recipients.supervisors.push({
            id: supervisor.id,
            name: supervisor.first_name && supervisor.last_name 
              ? `${supervisor.first_name} ${supervisor.last_name}` 
              : supervisor.username,
            email: supervisor.email,
            role: 'Supervisor',
            type: 'supervisor'
          });
        });
      }
      
      if (notifyCoordinators) {
        coordinators.forEach(coordinator => {
          recipients.supervisors.push({
            id: coordinator.id,
            name: coordinator.first_name && coordinator.last_name 
              ? `${coordinator.first_name} ${coordinator.last_name}` 
              : coordinator.username,
            email: coordinator.email,
            role: 'Coordinator',
            type: 'supervisor'
          });
        });
      }
    }
    
    // Add additional supervisors - they inherit the same context as their linked supervisor
    Object.keys(additionalSupervisors).forEach(supervisorId => {
      const originalSupervisor = recipients.supervisors.find(s => s.id === parseInt(supervisorId));
      if (originalSupervisor) {
        const additionalUsers = additionalSupervisors[supervisorId] || [];
        additionalUsers.forEach(user => {
          recipients.supervisors.push({
            ...originalSupervisor, // Copy all properties from original supervisor
            id: user.id,
            uniqueKey: `${user.id}_additional_for_${supervisorId}`,
            name: user.first_name && user.last_name 
              ? `${user.first_name} ${user.last_name}` 
              : user.username,
            email: user.email,
            role: `Additional (for ${originalSupervisor.name})`,
            originalSupervisorId: parseInt(supervisorId), // Add reference to original supervisor
            originalSupervisorRole: originalSupervisor.role // Add original supervisor role
          });
        });
      }
    });
    
    // Add training center
    if (notifyTrainingCenter && trainingData?.training_center_qualification_data?.training_center) {
      const tc = trainingData.training_center_qualification_data.training_center;
      recipients.trainingCenter = {
        id: tc.id,
        name: tc.name,
        email: tc.email || 'No email available',
        type: 'training_center'
      };
    }
    
    return recipients;
  };

  // Handle select all supervisors
  const handleSelectAllSupervisors = (checked) => {
    setSelectAllSupervisors(checked);
    setNotifyManagers(checked && managers.length > 0);
    setNotifySupervisors(checked && supervisors.length > 0);
    setNotifyCoordinators(checked && coordinators.length > 0);
  };
  
  // Update select all checkbox when individual checkboxes change
  useEffect(() => {
    const allSelected = 
      (managers.length === 0 || notifyManagers) &&
      (supervisors.length === 0 || notifySupervisors) &&
      (coordinators.length === 0 || notifyCoordinators) &&
      (managers.length > 0 || supervisors.length > 0 || coordinators.length > 0);
    
    setSelectAllSupervisors(allSelected);
  }, [notifyManagers, notifySupervisors, notifyCoordinators, managers.length, supervisors.length, coordinators.length]);

  const selectedRecipients = getSelectedRecipientsWithIds();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      // Allow closing when not sending
      if (!open && !sending) {
        onClose();
      }
    }}>
      <DialogContent className={`${selectedMessageRecipient ? 'sm:max-w-[1800px]' : 'sm:max-w-[1200px]'} h-[100vh] min-h-[100vh] max-h-[100vh] flex flex-col`} onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className={`${selectedMessageRecipient ? 'flex gap-6 items-center' : 'flex gap-6 items-center'}`}>
            <div className="flex-1">
              <DialogTitle className="flex items-center">
                <Mail className="h-5 w-5 mr-2 text-brand-500" />
                <span>Send Training {statusChange?.type === 'reminder' ? 'Reminder' : statusChange?.to === 'approved' ? 'Invitation' : statusChange?.to === 'cancelled' ? 'Cancellation' : statusChange?.type === 'changes' ? 'Changes Notification' : 'Notification'} Emails</span>
              </DialogTitle>
            </div>
            {selectedMessageRecipient && (
              <>
                <div className="flex-1">
                  <h3 className="text-lg font-medium">Email Recipients Preview</h3>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium">Message Preview</h3>
                </div>
              </>
            )}
          </div>
        </DialogHeader>
        
        <div className={`${selectedMessageRecipient ? 'flex gap-6' : 'flex gap-6'} flex-1 overflow-hidden`}>
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-6">
          {/* Training Information */}
          <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
            <h2 className="text-lg font-semibold text-brand-800 mb-2">
              Training: {trainingData?.training_center_qualification_data?.qualification_type?.qualification_type_data?.name || 'Unknown Qualification'} at {trainingData?.training_center_qualification_data?.training_center?.name || 'Unknown Training Center'}
            </h2>
            <p className="text-sm text-brand-600">
              {getStatusChangeMessage()}
            </p>
          </div>

          {/* Changes Information - Only show for changes mode without participant changes */}
          {statusChange?.type === 'changes' && !statusChange.participantChanges?.hasChanges && statusChange.changes && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-yellow-900 mb-3">
                ⚠️ Detected Changes ({statusChange.changes.length})
              </h3>
              <div className="space-y-3">
                {statusChange.changes.map((change, index) => (
                  <div key={index} className="bg-white border border-yellow-200 rounded p-3">
                    <div className="font-medium text-yellow-900 mb-2">{change.label}</div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-red-600 font-medium">From:</span>
                        <div className="text-red-700 mt-1 p-2 bg-red-50 rounded border border-red-200">
                          {change.oldValue}
                        </div>
                      </div>
                      <div>
                        <span className="text-green-600 font-medium">To:</span>
                        <div className="text-green-700 mt-1 p-2 bg-green-50 rounded border border-green-200">
                          {change.newValue}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Participant Changes Information - Don't show in hybrid mode - info is already in participant sections */}
          {false && (
            <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-brand-800 mb-3">
                👥 Participant Changes
              </h3>
              
              {statusChange.participantChanges.added?.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-green-800 mb-2">✅ Added Participants ({statusChange.participantChanges.added.length})</h4>
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <ul className="text-sm text-green-700">
                      {statusChange.participantChanges.added.map(user => (
                        <li key={user.id} className="flex items-center py-1">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                          {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username}
                          {user.email && <span className="text-green-600 ml-2">({user.email})</span>}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-green-600 mt-2 font-medium">→ Will receive invitation emails</p>
                  </div>
                </div>
              )}

              {statusChange.participantChanges.removed?.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-800 mb-2">❌ Removed Participants ({statusChange.participantChanges.removed.length})</h4>
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <ul className="text-sm text-red-700">
                      {statusChange.participantChanges.removed.map(user => (
                        <li key={user.id} className="flex items-center py-1">
                          <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                          {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username}
                          {user.email && <span className="text-red-600 ml-2">({user.email})</span>}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-red-600 mt-2 font-medium">→ Will receive cancellation emails</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {/* Progress Bar - Show during sending */}
          {sending && (
            <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
              <div className="flex items-center mb-3">
                <div className="relative mr-3">
                  {/* Windmill Animation */}
                  <div className="w-8 h-8 relative">
                    <svg 
                      className="w-8 h-8 animate-spin text-brand-500" 
                      viewBox="0 0 24 24" 
                      fill="currentColor"
                    >
                      {/* Windmill blades */}
                      <path d="M12 2L8 8h8l-4-6z" />
                      <path d="M22 12l-6 4v-8l6 4z" />
                      <path d="M12 22l4-6H8l4 6z" />
                      <path d="M2 12l6-4v8l-6-4z" />
                      {/* Center */}
                      <circle cx="12" cy="12" r="2" />
                      {/* Tower */}
                      <rect x="11" y="14" width="2" height="8" />
                    </svg>
                  </div>
                </div>
                <span className="text-brand-600 font-medium">Sending emails...</span>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-brand-200 rounded-full h-3 mb-2">
                <div 
                  className="bg-brand-500 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${sendingProgress}%` }}
                />
              </div>
              
              {/* Step Description */}
              <div className="text-sm text-brand-500 mb-1">
                {sendingStep}
              </div>
              
              {/* Progress Info */}
              <div className="flex justify-between text-xs text-brand-400">
                <span>{sendingProgress}% complete</span>
                {totalEmails > 0 && (
                  <span>{sentEmails.length} of {totalEmails} emails</span>
                )}
              </div>
              
              {/* Recent emails sent */}
              {sentEmails.length > 0 && (
                <div className="mt-3 max-h-24 overflow-y-auto">
                  <div className="text-xs text-brand-500 mb-1">Recently sent:</div>
                  {sentEmails.slice(-3).reverse().map((email, index) => (
                    <div key={index} className={`text-xs py-1 px-2 rounded mb-1 ${
                      email.error 
                        ? 'bg-red-100 text-red-700 border border-red-200' 
                        : 'bg-white text-brand-700 border border-brand-200'
                    }`}>
                      <div className="truncate">{email.recipient}</div>
                      <div className="text-brand-400">{email.timestamp}</div>
                    </div>
                  ))}
                  {sentEmails.length > 3 && (
                    <div className="text-xs text-brand-400">
                      ... and {sentEmails.length - 3} more
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="space-y-4">
          
            {/* Render different UI based on mode */}
            {(statusChange?.type === 'participant_changes' || (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges)) ? (
              // Participant Changes Mode UI
              <>
                {/* Added Participants Section */}
                {statusChange.participantChanges?.added?.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={statusChange?.type === 'participant_changes' ? true : 
                                  (participantChangeMode === 'all' || participantChangeMode === 'added')}
                          onChange={(e) => {
                            if (statusChange?.type === 'changes') {
                              const checked = e.target.checked;
                              if (checked) {
                                // Add added participants to selection
                                const addedIds = statusChange.participantChanges.added.map(u => u.id);
                                setSelectedParticipants(prev => new Set([...prev, ...addedIds]));
                                if (participantChangeMode !== 'all') {
                                  setParticipantChangeMode('added');
                                }
                              } else {
                                // Remove added participants from selection
                                const addedIds = statusChange.participantChanges.added.map(u => u.id);
                                setSelectedParticipants(prev => {
                                  const newSet = new Set(prev);
                                  addedIds.forEach(id => newSet.delete(id));
                                  return newSet;
                                });
                                if (participantChangeMode === 'all') {
                                  setParticipantChangeMode(null);
                                }
                              }
                            }
                          }}
                          disabled={statusChange?.type === 'participant_changes'}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-steel-300 rounded mr-3"
                        />
                        <Users className="h-4 w-4 mr-2 text-green-600" />
                        <span className="font-medium text-slate-900">
                          Added Participants ({statusChange.participantChanges.added.length})
                        </span>
                      </label>
                      <span className="text-sm text-slate-600">
                        ({(() => {
                          const addedIds = statusChange.participantChanges.added.map(u => u.id);
                          const selectedAddedCount = addedIds.filter(id => selectedParticipants.has(id)).length;
                          return selectedAddedCount;
                        })()}/{statusChange.participantChanges.added.length} selected)
                      </span>
                    </div>
                    
                    {notifyParticipants && participantChangeMode === 'added' && (
                      <div className="mb-3 text-xs text-slate-600">
                        Template: Training Invitation
                      </div>
                    )}

                    {/* Individual participant checkboxes for added */}
                    {statusChange.participantChanges.added.length > 0 && (participantChangeMode === 'all' || participantChangeMode === 'added') && (
                      <div className="border-t border-green-200 pt-3 mt-3">
                        <div className="max-h-32 overflow-y-auto space-y-1 ml-2">
                          {statusChange.participantChanges.added.map(participant => (
                            <label key={participant.id} className="flex items-center text-sm">
                              <div className="w-4"></div>
                              <input
                                type="checkbox"
                                checked={selectedParticipants.has(participant.id)}
                                onChange={() => handleParticipantToggle(participant.id)}
                                className="h-3 w-3 text-green-600 focus:ring-green-500 border-steel-300 rounded mr-2"
                              />
                              <span className="text-slate-700">
                                {participant.first_name && participant.last_name 
                                  ? `${participant.first_name} ${participant.last_name}` 
                                  : participant.username}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Removed Participants Section */}
                {statusChange.participantChanges?.removed?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={statusChange?.type === 'participant_changes' ? true : 
                                  (participantChangeMode === 'all' || participantChangeMode === 'removed')}
                          onChange={(e) => {
                            if (statusChange?.type === 'changes') {
                              const checked = e.target.checked;
                              if (checked) {
                                // Add removed participants to selection
                                const removedIds = statusChange.participantChanges.removed.map(u => u.id);
                                setSelectedParticipants(prev => new Set([...prev, ...removedIds]));
                                if (participantChangeMode !== 'all') {
                                  setParticipantChangeMode('removed');
                                }
                              } else {
                                // Remove removed participants from selection
                                const removedIds = statusChange.participantChanges.removed.map(u => u.id);
                                setSelectedParticipants(prev => {
                                  const newSet = new Set(prev);
                                  removedIds.forEach(id => newSet.delete(id));
                                  return newSet;
                                });
                                if (participantChangeMode === 'all') {
                                  setParticipantChangeMode(null);
                                }
                              }
                            }
                          }}
                          disabled={statusChange?.type === 'participant_changes'}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-steel-300 rounded mr-3"
                        />
                        <Users className="h-4 w-4 mr-2 text-red-600" />
                        <span className="font-medium text-slate-900">
                          Removed Participants ({statusChange.participantChanges.removed.length})
                        </span>
                      </label>
                      <span className="text-sm text-slate-600">
                        ({(() => {
                          const removedIds = statusChange.participantChanges.removed.map(u => u.id);
                          const selectedRemovedCount = removedIds.filter(id => selectedParticipants.has(id)).length;
                          return selectedRemovedCount;
                        })()}/{statusChange.participantChanges.removed.length} selected)
                      </span>
                    </div>
                    
                    {notifyParticipants && participantChangeMode === 'removed' && (
                      <div className="mb-3 text-xs text-slate-600">
                        Template: Training Cancellation
                      </div>
                    )}

                    {/* Individual participant checkboxes for removed */}
                    {statusChange.participantChanges.removed.length > 0 && (participantChangeMode === 'all' || participantChangeMode === 'removed') && (
                      <div className="border-t border-red-200 pt-3 mt-3">
                        <div className="max-h-32 overflow-y-auto space-y-1 ml-2">
                          {statusChange.participantChanges.removed.map(participant => (
                            <label key={participant.id} className="flex items-center text-sm">
                              <div className="w-4"></div>
                              <input
                                type="checkbox"
                                checked={selectedParticipants.has(participant.id)}
                                onChange={() => handleParticipantToggle(participant.id)}
                                className="h-3 w-3 text-red-600 focus:ring-red-500 border-steel-300 rounded mr-2"
                              />
                              <span className="text-slate-700">
                                {participant.first_name && participant.last_name 
                                  ? `${participant.first_name} ${participant.last_name}` 
                                  : participant.username}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Participants with Changes Section - Only for hybrid mode */}
                {statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges && (() => {
                  // Calculate unchanged participants
                  const addedIds = statusChange.participantChanges?.added?.map(u => u.id) || [];
                  const removedIds = statusChange.participantChanges?.removed?.map(u => u.id) || [];
                  const unchangedParticipants = trainingData?.users_data?.filter(user => 
                    !addedIds.includes(user.id) && !removedIds.includes(user.id)
                  ) || [];
                  
                  if (unchangedParticipants.length > 0) {
                    return (
                      <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={participantChangeMode === 'all' || participantChangeMode === 'changes'}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                if (checked) {
                                  // Add unchanged participants to selection
                                  const unchangedIds = unchangedParticipants.map(u => u.id);
                                  setSelectedParticipants(prev => new Set([...prev, ...unchangedIds]));
                                  if (participantChangeMode !== 'all') {
                                    setParticipantChangeMode('changes');
                                  }
                                } else {
                                  // Remove unchanged participants from selection
                                  const unchangedIds = unchangedParticipants.map(u => u.id);
                                  setSelectedParticipants(prev => {
                                    const newSet = new Set(prev);
                                    unchangedIds.forEach(id => newSet.delete(id));
                                    return newSet;
                                  });
                                  if (participantChangeMode === 'all') {
                                    setParticipantChangeMode(null);
                                  }
                                }
                              }}
                              className="h-4 w-4 text-brand-500 focus:ring-brand-500 border-steel-300 rounded mr-3"
                            />
                            <Users className="h-4 w-4 mr-2 text-brand-500" />
                            <span className="font-medium text-slate-900">
                              Participants with Changes ({unchangedParticipants.length})
                            </span>
                          </label>
                          <span className="text-sm text-slate-600">
                            ({(() => {
                              const unchangedIds = unchangedParticipants.map(u => u.id);
                              const selectedUnchangedCount = unchangedIds.filter(id => selectedParticipants.has(id)).length;
                              return selectedUnchangedCount;
                            })()}/{unchangedParticipants.length} selected)
                          </span>
                        </div>
                        
                        {(participantChangeMode === 'all' || participantChangeMode === 'changes') && (
                          <div className="mb-3 text-xs text-slate-600">
                            Template: Training Changes Notification
                          </div>
                        )}

                        {/* Individual participant checkboxes - always show if there are multiple participants */}
                        {unchangedParticipants.length > 0 && (participantChangeMode === 'all' || participantChangeMode === 'changes') && (
                          <div className="border-t border-brand-200 pt-3 mt-3">
                            <div className="max-h-32 overflow-y-auto space-y-1 ml-2">
                              {unchangedParticipants.map(participant => (
                                <label key={participant.id} className="flex items-center text-sm">
                                  <div className="w-4"></div>
                                  <input
                                    type="checkbox"
                                    checked={selectedParticipants.has(participant.id)}
                                    onChange={() => handleParticipantToggle(participant.id)}
                                    className="h-3 w-3 text-brand-500 focus:ring-brand-500 border-steel-300 rounded mr-2"
                                  />
                                  <span className="text-slate-700">
                                    {participant.first_name && participant.last_name 
                                      ? `${participant.first_name} ${participant.last_name}` 
                                      : participant.username}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Supervisors & Managers & Coordinators */}
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={
                          (notifyManagersAdded || notifyManagersRemoved || notifyManagersChanges) ||
                          (notifySupervisorsAdded || notifySupervisorsRemoved || notifySupervisorsChanges) ||
                          (notifyCoordinatorsAdded || notifyCoordinatorsRemoved || notifyCoordinatorsChanges)
                        }
                        onChange={(e) => {
                          const checked = e.target.checked;
                          // Select all supervisor options when main checkbox is checked
                          setNotifyManagersAdded(checked);
                          setNotifyManagersRemoved(checked);
                          setNotifySupervisorsAdded(checked);
                          setNotifySupervisorsRemoved(checked);
                          setNotifyCoordinatorsAdded(checked);
                          setNotifyCoordinatorsRemoved(checked);
                          // Also handle changes mode in hybrid scenarios
                          if (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges) {
                            setNotifyManagersChanges(checked);
                            setNotifySupervisorsChanges(checked);
                            setNotifyCoordinatorsChanges(checked);
                          }
                        }}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-steel-300 rounded mr-3"
                      />
                      <UserCheck className="h-4 w-4 mr-2 text-purple-600" />
                      <span className="font-medium text-slate-900">Supervisors & Managers & Coordinators</span>
                    </label>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-slate-600">
                        ({(notifyManagersAdded ? managersAdded.length : 0) + 
                          (notifyManagersRemoved ? managersRemoved.length : 0) + 
                          (notifyManagersChanges ? managersChanges.length : 0) + 
                          (notifySupervisorsAdded ? supervisorsAdded.length : 0) + 
                          (notifySupervisorsRemoved ? supervisorsRemoved.length : 0) + 
                          (notifySupervisorsChanges ? supervisorsChanges.length : 0) + 
                          (notifyCoordinatorsAdded ? coordinatorsAdded.length : 0) + 
                          (notifyCoordinatorsRemoved ? coordinatorsRemoved.length : 0) + 
                          (notifyCoordinatorsChanges ? coordinatorsChanges.length : 0)} selected)
                      </span>
                      {((managersAdded.length + managersRemoved.length + managersChanges.length + 
                         supervisorsAdded.length + supervisorsRemoved.length + supervisorsChanges.length + 
                         coordinatorsAdded.length + coordinatorsRemoved.length + coordinatorsChanges.length) > 0) && (
                        <button
                          onClick={() => setManagersExpanded(!managersExpanded)}
                          className="ml-2 p-1 hover:bg-purple-100 rounded"
                        >
                          {managersExpanded ? (
                            <ChevronUp className="h-4 w-4 text-purple-600" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-purple-600" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {managersExpanded && (
                    <div className="border-t border-purple-200 pt-3 mt-3">
                      <div className="space-y-4 ml-2">
                        {/* Managers */}
                        {(managersAdded.length > 0 || managersRemoved.length > 0 || managersChanges.length > 0) && (
                          <div>
                            <h5 className="font-medium text-sm text-slate-700 mb-2">
                              Managers ({(() => {
                                const counts = [];
                                if (managersAdded.length > 0) counts.push(`${managersAdded.length} for added`);
                                if (managersRemoved.length > 0) counts.push(`${managersRemoved.length} for removed`);
                                if (managersChanges.length > 0) counts.push(`${managersChanges.length} for changes`);
                                return counts.length > 1 ? counts.join(', ') : (counts[0] || '0');
                              })()})
                            </h5>
                            <div className="space-y-2 ml-4">
                              {managersAdded.length > 0 && (
                                <label className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={notifyManagersAdded}
                                      onChange={(e) => setNotifyManagersAdded(e.target.checked)}
                                      className="h-3 w-3 text-green-600 focus:ring-green-500 border-steel-300 rounded mr-2"
                                    />
                                    <span className="text-sm text-green-700">For Added Participants</span>
                                  </div>
                                  <span className="text-xs text-green-600">Template: Employee Training Invitation</span>
                                </label>
                              )}
                              {managersRemoved.length > 0 && (
                                <label className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={notifyManagersRemoved}
                                      onChange={(e) => setNotifyManagersRemoved(e.target.checked)}
                                      className="h-3 w-3 text-red-600 focus:ring-red-500 border-steel-300 rounded mr-2"
                                    />
                                    <span className="text-sm text-red-700">For Removed Participants</span>
                                  </div>
                                  <span className="text-xs text-red-600">Template: Employee Training Cancellation</span>
                                </label>
                              )}
                              {/* Show changes option only in hybrid mode */}
                              {statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges && managersChanges.length > 0 && (
                                <label className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={notifyManagersChanges}
                                      onChange={(e) => setNotifyManagersChanges(e.target.checked)}
                                      className="h-3 w-3 text-brand-500 focus:ring-brand-500 border-steel-300 rounded mr-2"
                                    />
                                    <span className="text-sm text-brand-600">For Unchanged Participants (Training Changes)</span>
                                  </div>
                                  <span className="text-xs text-brand-500">Template: Employee Training Changes</span>
                                </label>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Supervisors */}
                        {(supervisorsAdded.length > 0 || supervisorsRemoved.length > 0 || supervisorsChanges.length > 0) && (
                          <div>
                            <h5 className="font-medium text-sm text-slate-700 mb-2">
                              Training Supervisors ({(() => {
                                const counts = [];
                                if (supervisorsAdded.length > 0) counts.push(`${supervisorsAdded.length} for added`);
                                if (supervisorsRemoved.length > 0) counts.push(`${supervisorsRemoved.length} for removed`);
                                if (supervisorsChanges.length > 0) counts.push(`${supervisorsChanges.length} for changes`);
                                return counts.length > 1 ? counts.join(', ') : (counts[0] || '0');
                              })()})
                            </h5>
                            <div className="space-y-2 ml-4">
                              {supervisorsAdded.length > 0 && (
                                <label className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={notifySupervisorsAdded}
                                      onChange={(e) => setNotifySupervisorsAdded(e.target.checked)}
                                      className="h-3 w-3 text-green-600 focus:ring-green-500 border-steel-300 rounded mr-2"
                                    />
                                    <span className="text-sm text-green-700">For Added Participants</span>
                                  </div>
                                  <span className="text-xs text-green-600">Template: Employee Training Invitation</span>
                                </label>
                              )}
                              {supervisorsRemoved.length > 0 && (
                                <label className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={notifySupervisorsRemoved}
                                      onChange={(e) => setNotifySupervisorsRemoved(e.target.checked)}
                                      className="h-3 w-3 text-red-600 focus:ring-red-500 border-steel-300 rounded mr-2"
                                    />
                                    <span className="text-sm text-red-700">For Removed Participants</span>
                                  </div>
                                  <span className="text-xs text-red-600">Template: Employee Training Cancellation</span>
                                </label>
                              )}
                              {/* Show changes option only in hybrid mode */}
                              {statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges && supervisorsChanges.length > 0 && (
                                <label className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={notifySupervisorsChanges}
                                      onChange={(e) => setNotifySupervisorsChanges(e.target.checked)}
                                      className="h-3 w-3 text-brand-500 focus:ring-brand-500 border-steel-300 rounded mr-2"
                                    />
                                    <span className="text-sm text-brand-600">For Unchanged Participants (Training Changes)</span>
                                  </div>
                                  <span className="text-xs text-brand-500">Template: Employee Training Changes</span>
                                </label>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Coordinators */}
                        {(coordinatorsAdded.length > 0 || coordinatorsRemoved.length > 0 || coordinatorsChanges.length > 0) && (
                          <div>
                            <h5 className="font-medium text-sm text-slate-700 mb-2">
                              Training Coordinators ({(() => {
                                const counts = [];
                                if (coordinatorsAdded.length > 0) counts.push(`${coordinatorsAdded.length} for added`);
                                if (coordinatorsRemoved.length > 0) counts.push(`${coordinatorsRemoved.length} for removed`);
                                if (coordinatorsChanges.length > 0) counts.push(`${coordinatorsChanges.length} for changes`);
                                return counts.length > 1 ? counts.join(', ') : (counts[0] || '0');
                              })()})
                            </h5>
                            <div className="space-y-2 ml-4">
                              {coordinatorsAdded.length > 0 && (
                                <label className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={notifyCoordinatorsAdded}
                                      onChange={(e) => setNotifyCoordinatorsAdded(e.target.checked)}
                                      className="h-3 w-3 text-green-600 focus:ring-green-500 border-steel-300 rounded mr-2"
                                    />
                                    <span className="text-sm text-green-700">For Added Participants</span>
                                  </div>
                                  <span className="text-xs text-green-600">Template: Employee Training Invitation</span>
                                </label>
                              )}
                              {coordinatorsRemoved.length > 0 && (
                                <label className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={notifyCoordinatorsRemoved}
                                      onChange={(e) => setNotifyCoordinatorsRemoved(e.target.checked)}
                                      className="h-3 w-3 text-red-600 focus:ring-red-500 border-steel-300 rounded mr-2"
                                    />
                                    <span className="text-sm text-red-700">For Removed Participants</span>
                                  </div>
                                  <span className="text-xs text-red-600">Template: Employee Training Cancellation</span>
                                </label>
                              )}
                              {/* Show changes option only in hybrid mode */}
                              {statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges && coordinatorsChanges.length > 0 && (
                                <label className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={notifyCoordinatorsChanges}
                                      onChange={(e) => setNotifyCoordinatorsChanges(e.target.checked)}
                                      className="h-3 w-3 text-brand-500 focus:ring-brand-500 border-steel-300 rounded mr-2"
                                    />
                                    <span className="text-sm text-brand-600">For Unchanged Participants (Training Changes)</span>
                                  </div>
                                  <span className="text-xs text-brand-500">Template: Employee Training Changes</span>
                                </label>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Training Center */}
                <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
                  <label className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={notifyTrainingCenter}
                        onChange={(e) => setNotifyTrainingCenter(e.target.checked)}
                        className="h-4 w-4 text-brand-500 focus:ring-brand-500 border-steel-300 rounded mr-3"
                      />
                      <Building className="h-4 w-4 mr-2 text-brand-500" />
                      <span className="font-medium text-slate-900">Training Center</span>
                    </div>
                    <span className="text-sm text-slate-600">
                      {trainingData?.training_center_qualification_data?.training_center?.name || 'Not specified'}
                    </span>
                  </label>
                  {notifyTrainingCenter && (
                    <div className="mt-2 text-xs text-slate-600">
                      Template: Participant List Update
                    </div>
                  )}
                </div>
              </>
            ) : (
            <>
            {/* Participants - Only show in non-hybrid changes mode */}
            <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
              
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={notifyParticipants}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setNotifyParticipants(checked);
                      if (checked) {
                        // Select all participants based on current mode
                        let participantIds;
                        if (participantChangeMode === 'added') {
                          participantIds = new Set(statusChange.participantChanges?.added?.map(u => u.id) || []);
                        } else if (participantChangeMode === 'removed') {
                          participantIds = new Set(statusChange.participantChanges?.removed?.map(u => u.id) || []);
                        } else {
                          participantIds = new Set(trainingData?.users_data?.map(u => u.id) || []);
                        }
                        setSelectedParticipants(participantIds);
                      } else {
                        // Deselect all participants
                        setSelectedParticipants(new Set());
                      }
                    }}
                    className="h-4 w-4 text-brand-500 focus:ring-brand-500 border-steel-300 rounded mr-3"
                  />
                  <Users className="h-4 w-4 mr-2 text-brand-500" />
                  <span className="font-medium text-slate-900">
                    {participantChangeMode === 'added' ? 'Added Participants' : 
                     participantChangeMode === 'removed' ? 'Removed Participants' : 
                     'All Training Participants'}
                  </span>
                </label>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-slate-600">
                    ({selectedParticipants.size}/
                    {participantChangeMode === 'added' ? statusChange.participantChanges?.added?.length || 0 :
                     participantChangeMode === 'removed' ? statusChange.participantChanges?.removed?.length || 0 :
                     trainingData?.users_data?.length || 0} selected)
                  </span>
                  {(trainingData?.users_data?.length || 0) > 1 && (
                    <button
                      onClick={() => setParticipantsExpanded(!participantsExpanded)}
                      className="ml-2 p-1 hover:bg-brand-100 rounded"
                    >
                      {participantsExpanded ? (
                        <ChevronUp className="h-4 w-4 text-brand-500" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-brand-500" />
                      )}
                    </button>
                  )}
                </div>
              </div>
              
              {notifyParticipants && (
                <div className="mb-3 text-xs text-slate-600">
                  Template: {templateTypes.participants === 'participants_invitation' ? 'Training Invitation' : 'Training Cancellation'}
                </div>
              )}

              {/* Expandable participant list */}
              {participantsExpanded && notifyParticipants && (trainingData?.users_data?.length || 0) > 1 && (
                <div className="border-t border-brand-200 pt-3 mt-3">
                  {/* Individual participant checkboxes */}
                  <div className="max-h-32 overflow-y-auto space-y-1 ml-2">
                    {(function() {
                      let participantsToShow = trainingData?.users_data || [];
                      if (participantChangeMode === 'added') {
                        participantsToShow = statusChange.participantChanges?.added || [];
                      } else if (participantChangeMode === 'removed') {
                        participantsToShow = statusChange.participantChanges?.removed || [];
                      }
                      return participantsToShow;
                    })().map(participant => (
                      <label key={participant.id} className="flex items-center text-sm">
                        <div className="w-4"></div>
                        <input
                          type="checkbox"
                          checked={selectedParticipants.has(participant.id)}
                          onChange={() => handleParticipantToggle(participant.id)}
                          className="h-3 w-3 text-brand-500 focus:ring-brand-500 border-steel-300 rounded mr-2"
                        />
                        <span className="text-slate-700">
                          {participant.first_name && participant.last_name 
                            ? `${participant.first_name} ${participant.last_name}` 
                            : participant.username}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Supervisors & Managers & Coordinators */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectAllSupervisors}
                    onChange={(e) => handleSelectAllSupervisors(e.target.checked)}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-steel-300 rounded mr-3"
                  />
                  <UserCheck className="h-4 w-4 mr-2 text-purple-600" />
                  <span className="font-medium text-slate-900">Supervisors & Managers & Coordinators</span>
                </label>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-slate-600">
                    ({(notifyManagers ? managers.length : 0) + 
                      (notifySupervisors ? supervisors.length : 0) + 
                      (notifyCoordinators ? coordinators.length : 0)}/
                     {managers.length + supervisors.length + coordinators.length} selected)
                  </span>
                  {(managers.length + supervisors.length + coordinators.length) > 0 && (
                    <button
                      onClick={() => setManagersExpanded(!managersExpanded)}
                      className="ml-2 p-1 hover:bg-purple-100 rounded"
                    >
                      {managersExpanded ? (
                        <ChevronUp className="h-4 w-4 text-purple-600" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-purple-600" />
                      )}
                    </button>
                  )}
                </div>
              </div>
              
              {selectAllSupervisors && (
                <div className="mb-3 text-xs text-slate-600">
                  Template: {templateTypes.supervisors === 'supervisors_invitation' ? 'Employee Training Invitation' : 'Employee Training Cancellation'}
                </div>
              )}

              {managersExpanded && (
                <div className="border-t border-purple-200 pt-3 mt-3">
                  <div className="space-y-2 ml-2">
                    {/* Managers */}
                    <label className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-4"></div>
                        <input
                          type="checkbox"
                          checked={notifyManagers}
                          onChange={(e) => setNotifyManagers(e.target.checked)}
                          disabled={managers.length === 0}
                          className="h-3 w-3 text-purple-600 focus:ring-purple-500 border-steel-300 rounded mr-2 disabled:opacity-50"
                        />
                        <span className={`text-sm ${managers.length === 0 ? 'text-slate-400' : 'text-slate-700'}`}>
                          Managers
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">({managers.length})</span>
                    </label>

                    {/* Supervisors */}
                    <label className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-4"></div>
                        <input
                          type="checkbox"
                          checked={notifySupervisors}
                          onChange={(e) => setNotifySupervisors(e.target.checked)}
                          disabled={supervisors.length === 0}
                          className="h-3 w-3 text-purple-600 focus:ring-purple-500 border-steel-300 rounded mr-2 disabled:opacity-50"
                        />
                        <span className={`text-sm ${supervisors.length === 0 ? 'text-slate-400' : 'text-slate-700'}`}>
                          Supervisors
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">({supervisors.length})</span>
                    </label>

                    {/* Coordinators */}
                    <label className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-4"></div>
                        <input
                          type="checkbox"
                          checked={notifyCoordinators}
                          onChange={(e) => setNotifyCoordinators(e.target.checked)}
                          disabled={coordinators.length === 0}
                          className="h-3 w-3 text-purple-600 focus:ring-purple-500 border-steel-300 rounded mr-2 disabled:opacity-50"
                        />
                        <span className={`text-sm ${coordinators.length === 0 ? 'text-slate-400' : 'text-slate-700'}`}>
                          Coordinators
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">({coordinators.length})</span>
                    </label>
                  </div>
                </div>
              )}

              {(managers.length === 0 && supervisors.length === 0 && coordinators.length === 0) && (
                <div className="mt-2 text-xs text-slate-500">
                  No managers, supervisors, or coordinators found for participants
                </div>
              )}
            </div>

            {/* Training Center - Only for approved status */}
            {statusChange?.to === 'approved' && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <label className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={notifyTrainingCenter}
                      onChange={(e) => setNotifyTrainingCenter(e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-steel-300 rounded mr-3"
                    />
                    <Building className="h-4 w-4 mr-2 text-green-600" />
                    <span className="font-medium text-slate-900">Training Center</span>
                  </div>
                  <span className="text-sm text-slate-600">
                    {trainingData?.training_center_qualification_data?.training_center?.name || 'Not specified'}
                  </span>
                </label>
                {notifyTrainingCenter && (
                  <div className="mt-2 text-xs text-slate-600">
                    Template: Training Participation Confirmation
                  </div>
                )}
              </div>
            )}
            </>
            )}
          </div>
        </div>
      </div>
      
      {/* Middle - Preview Panel */}
      <div className="flex-1 border-l border-steel-200 overflow-y-auto flex flex-col">
        <div className="flex-1 p-4 overflow-y-auto">
          
          <div className="space-y-0">
            {/* Participants Section */}
            <div className="pb-4 border-b border-steel-200">
              <h4 className="font-medium text-sm text-slate-700 mb-2 flex items-center">
                <Users className="h-4 w-4 mr-1 text-brand-500" />
                Participants ({selectedRecipients.participants.length})
              </h4>
              {selectedRecipients.participants.length > 0 ? (
                <div className="max-h-72 overflow-y-auto">
                  <ul className="space-y-2">
                    {selectedRecipients.participants.map((participant, idx) => (
                      <li key={idx} className="text-sm ml-2">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-slate-800 truncate">{participant.name}</div>
                              {(statusChange?.type === 'participant_changes' || (statusChange?.type === 'changes' && statusChange.participantChanges?.hasChanges)) && participant.changeType && (
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                  participant.changeType === 'added' 
                                    ? 'bg-green-100 text-green-800' 
                                    : participant.changeType === 'removed'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-brand-100 text-brand-700'
                                }`}>
                                  {participant.changeType === 'added' ? 'Added' : 
                                   participant.changeType === 'removed' ? 'Removed' : 'Changes'}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 truncate">{participant.email}</div>
                          </div>
                          <button
                            onClick={() => handleShowMessage(participant)}
                            className="ml-2 px-2 py-1 text-xs bg-brand-100 text-brand-600 rounded hover:bg-brand-200 flex items-center"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Show Message
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">No participants selected</p>
              )}
            </div>
            
            {/* Supervisors & Managers & Coordinators Section */}
            <div className="py-4 border-b border-steel-200">
              <h4 className="font-medium text-sm text-slate-700 mb-2 flex items-center">
                <UserCheck className="h-4 w-4 mr-1 text-purple-600" />
                Supervisors & Managers & Coordinators ({selectedRecipients.supervisors.length})
              </h4>
              {selectedRecipients.supervisors.length > 0 ? (
                <div className="max-h-72 overflow-y-auto">
                  <ul className="space-y-2">
                    {selectedRecipients.supervisors.map((supervisor) => (
                      <li key={supervisor.uniqueKey || supervisor.id} className="text-sm ml-2">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-800 truncate">
                              {supervisor.name} <span className="text-xs text-slate-500">({supervisor.role})</span>
                            </div>
                            <div className="text-xs text-slate-500 truncate">{supervisor.email}</div>
                            {/* Show notification types for participant_changes mode */}
                            {statusChange?.type === 'participant_changes' && supervisor.notificationTypes && (
                              <div className="flex gap-2 mt-1">
                                {supervisor.notificationTypes.added && (
                                  <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                    For Added
                                  </span>
                                )}
                                {supervisor.notificationTypes.removed && (
                                  <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                    For Removed
                                  </span>
                                )}
                                {supervisor.notificationTypes.changes && (
                                  <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-brand-100 text-brand-700">
                                    For Changes
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => openUserSelectorForSupervisor(supervisor)}
                              className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 flex items-center"
                              title="Add additional recipient"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => handleShowMessage(supervisor)}
                              className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 flex items-center"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Show Message
                            </button>
                          </div>
                        </div>
                        {/* Show additional recipients for this supervisor */}
                        {additionalSupervisors[supervisor.id]?.length > 0 && (
                          <div className="ml-4 mt-2 p-2 bg-purple-50 rounded">
                            <div className="text-xs font-medium text-purple-700 mb-1">Additional recipients (will receive same email):</div>
                            <div className="space-y-1">
                              {additionalSupervisors[supervisor.id].map(addUser => (
                                <div key={addUser.id} className="flex items-center justify-between text-xs">
                                  <span className="text-slate-700">
                                    {addUser.first_name && addUser.last_name 
                                      ? `${addUser.first_name} ${addUser.last_name}` 
                                      : addUser.username} ({addUser.email})
                                  </span>
                                  <button
                                    onClick={() => removeAdditionalSupervisor(supervisor.id, addUser.id)}
                                    className="text-purple-600 hover:text-purple-800 ml-2"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">No supervisors selected</p>
              )}
            </div>
            
            {/* Training Center Section */}
            {(statusChange?.to === 'approved' || statusChange?.type === 'participant_changes' || statusChange?.type === 'changes') && (
              <div className="pt-4">
                <h4 className="font-medium text-sm text-slate-700 mb-2 flex items-center">
                  <Building className="h-4 w-4 mr-1 text-green-600" />
                  Training Center
                </h4>
                {selectedRecipients.trainingCenter ? (
                  <div className="text-sm ml-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 truncate">{selectedRecipients.trainingCenter.name}</div>
                        <div className="text-xs text-slate-500 truncate">{selectedRecipients.trainingCenter.email}</div>
                      </div>
                      <button
                        onClick={() => handleShowMessage(selectedRecipients.trainingCenter)}
                        className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 flex items-center"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Show Message
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">No training center selected</p>
                )}
              </div>
            )}
            
          </div>
        </div>
      </div>
        
        {/* Right side - Message Preview Panel - Only show when message is selected */}
        {selectedMessageRecipient && (
          <div className="flex-1 p-4 border-l border-steel-200 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <button
                onClick={() => setSelectedMessageRecipient(null)}
                className="text-slate-500 hover:text-slate-700 text-sm ml-auto"
              >
                ✕ Close
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-4 border">
                <div className="mb-3">
                  <div className="font-medium text-slate-800">{selectedMessageRecipient.name}</div>
                  <div className="text-sm text-slate-600">{selectedMessageRecipient.email}</div>
                  {selectedMessageRecipient.role && (
                    <div className="text-xs text-slate-500">Role: {selectedMessageRecipient.role}</div>
                  )}
                </div>
                
                {loadingPreview ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
                    <span className="ml-2 text-sm text-slate-600">Loading email preview...</span>
                  </div>
                ) : templatePreview ? (
                  templatePreview.error ? (
                    <div className="space-y-2">
                      <div className="text-sm text-red-600">{templatePreview.error}</div>
                      {templatePreview.details && (
                        <div className="text-xs text-slate-500">{templatePreview.details}</div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-slate-700">From:</label>
                        <div className="mt-1 p-2 bg-steel-50 rounded text-sm">
                          {templatePreview.from_email || 'noreply@ventro.energy'}
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-slate-700">To:</label>
                        <div className="mt-1 p-2 bg-steel-50 rounded text-sm">
                          {selectedMessageRecipient.email}
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-slate-700">Subject:</label>
                        <div className="mt-1 p-2 bg-steel-50 rounded text-sm">{templatePreview.subject}</div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-slate-700">Message:</label>
                        <div className="mt-1 p-3 bg-steel-50 rounded text-sm">
                          {templatePreview.body_html ? (
                            <div 
                              className="email-preview max-h-96 overflow-y-auto"
                              dangerouslySetInnerHTML={{ __html: templatePreview.html_content }}
                            />
                          ) : (
                            <div className="whitespace-pre-line">{templatePreview.text_content}</div>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-xs text-slate-500 border-t pt-2">
                        <div>Template: {templatePreview.template_name || templatePreview.template_type}</div>
                        <div>Language: {templatePreview.language || 'Default'}</div>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="text-sm text-slate-500 italic">Select a recipient to preview the email</div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
        
        <DialogFooter className="flex-shrink-0 mt-4 border-t pt-4">
          <div className="w-full flex justify-between items-center">
            <div className="text-sm font-medium text-slate-700">
              Total recipients: {selectedRecipients.participants.length + selectedRecipients.supervisors.length + (selectedRecipients.trainingCenter ? 1 : 0)}
            </div>
            <div className="flex space-x-2">
            <Button variant="outline" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button 
              className="bg-brand-500 hover:bg-brand-600 text-white"
              onClick={handleSendEmails}
              disabled={sending || (
                (!notifyParticipants || selectedParticipants.size === 0) && 
                !notifyManagers && 
                !notifySupervisors && 
                !notifyCoordinators &&
                !notifyTrainingCenter &&
                Object.keys(additionalSupervisors).length === 0
              )}
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Emails
                </>
              )}
            </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
      
      {/* User Selector Modal */}
      {showUserSelector && selectedSupervisorForAdditional && (
        <Dialog open={showUserSelector} onOpenChange={setShowUserSelector}>
          <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Add Additional Recipient</DialogTitle>
              <div className="space-y-1">
                <p className="text-sm text-slate-600">
                  Select an additional person who will receive the same email as:
                </p>
                <div className="bg-purple-50 p-2 rounded">
                  <div className="font-medium text-purple-900">
                    {selectedSupervisorForAdditional.name} ({selectedSupervisorForAdditional.role})
                  </div>
                  <div className="text-xs text-purple-700">{selectedSupervisorForAdditional.email}</div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  This is useful for notifying deputies or backup supervisors.
                </p>
              </div>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto min-h-0 py-4">
              {loadingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                </div>
              ) : (
                <div className="space-y-2">
                  {getAvailableUsers().length === 0 ? (
                    <div className="text-sm text-slate-500 text-center py-4">
                      No additional users available to select
                    </div>
                  ) : (
                    getAvailableUsers().map(user => (
                      <button
                        key={user.id}
                        onClick={() => addAdditionalSupervisor(user)}
                        className="w-full text-left px-3 py-2 rounded-xl border border-steel-200 hover:bg-purple-50 hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                      >
                        <div className="font-medium text-slate-900">
                          {user.first_name && user.last_name 
                            ? `${user.first_name} ${user.last_name}` 
                            : user.username}
                        </div>
                        <div className="text-sm text-slate-600">{user.email}</div>
                        {user.position && (
                          <div className="text-xs text-slate-500 mt-1">{user.position}</div>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUserSelector(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};

export default TrainingMailSender;